import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector, isDevMode, NgZone } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { FailClosedService } from 'app/core/error/fail-closed.service';
import { SnackBarService } from 'app/core/snack-bar/snack-bar.service';
import { safeErrorText } from 'app/modules/shared/utils/safe-log';

/** At most one notice per this many milliseconds, so a failing loop cannot cover the screen. */
const NOTICE_INTERVAL_MS = 15000;

/**
 * The one place where every exception that nothing else caught ends up (BSI TR-03161-2 O.Source_4):
 *
 *  - it is reported to Sentry (production builds), with tokens removed (see main.ts);
 *  - it is written to the console as a short description only - no stack trace and no request address, which can
 *    contain a token (a developer machine, `ng serve`, still gets the full error);
 *  - the user gets a short, translated notice and never the technical text.
 *
 * Errors that a component handles itself (an error callback, catchError) do not reach this handler.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    private readonly _injector = inject(Injector);
    private readonly _sentry = Sentry.createErrorHandler({ logErrors: false });
    private _lastNotice = 0;
    private _securityNoticeShown = false;

    handleError(error: unknown): void {
        try {
            this._sentry.handleError(error);
        } catch {
            // reporting must never make things worse
        }

        if (isDevMode()) {
            console.error(error);
        } else {
            console.error('Unhandled error:', safeErrorText(error));
        }

        if(error?.toString()?.includes("ExpressionChangedAfterItHasBeenCheckedError")){
            return;
        }

        // delete short-lived sensitive data; when errors keep coming, cancel access altogether (BSI O.Source_5)
        let accessCancelled = false;
        try {
            accessCancelled = this._injector.get(FailClosedService).onUnhandledError(error);
        } catch {
            // the error handler must never throw
        }
        if (accessCancelled) {
            this._notifySecurity();
            return;
        }

        this._notifyUser(error);
    }

    private _notifySecurity(): void {
        if (this._securityNoticeShown) {
            return;
        }
        this._securityNoticeShown = true;
        try {
            this._injector.get(NgZone).run(() => this._injector.get(SnackBarService).openTranslatedSnackBar('ERROR-SIGNED-OUT-FOR-SECURITY'));
        } catch {
            // the notice is a courtesy
        }
    }

    private _notifyUser(error: unknown): void {
        // the sign-in flow reacts to 401/403 on its own
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            return;
        }
        const now = Date.now();
        if (now - this._lastNotice < NOTICE_INTERVAL_MS) {
            return;
        }
        this._lastNotice = now;

        try {
            this._injector.get(NgZone).run(() => this._injector.get(SnackBarService).openErrorSnackBar(error));
        } catch {
            // the notice is a courtesy: if it cannot be shown there is nothing else to do
        }
    }
}
